import { Injectable } from '@angular/core';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { CrudService } from '@app/dao/CrudService';
import { DocumentDao } from '@app/dao/document/document.dao';
import { PledgeDao } from '@app/dao/pledge/pledge.dao';
import { Question } from '@app/forms/subscription/definition/interface';
import {
  AdditionalQuestionsObject,
  Owner,
  Parameters,
  PledgeInitParams,
  PledgeRead,
  Product,
} from '@app/forms/subscription/definition/interface/Pledge';
import {
  CommandEnum,
  SubscriberTypeEnum,
} from '@app/forms/subscription/definition/interface/enum';
import { PledgeOptions } from '@app/forms/subscription/definition/interface/pledge-options';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject, Subscription, finalize, map, mergeMap, take, tap } from 'rxjs';
import { EventBusService } from './event-bus.service';
import { LoadingService } from './loading.service';

export type SubmitionType =
  | 'pledge'
  | 'products'
  | 'owners'
  | 'pledgeAndResetProduct'
  | 'accountInformations';
type DataType = Owner[] | Product[] | Parameters;
type DeepReadonly<T> = { readonly [P in keyof T]: T[P] };
export type HttpRequestState = DeepReadonly<
  'EMPTY' | 'FETCHING' | 'FETCHED' | { errorMessage: string }
>;
@Injectable({
  providedIn: 'root',
})
export class EntityService {
  private ownerSubject$ = new BehaviorSubject<Owner[]>(null);
  ownerChanged$ = this.ownerSubject$.asObservable();
  private commandsSubject$ = new BehaviorSubject<CommandEnum[]>(null);
  commandsChanged$ = this.commandsSubject$.asObservable();

  private stepNameSubject$ = new BehaviorSubject<string>(null);
  stepNameChanged$ = this.stepNameSubject$.asObservable();

  private questionsSubject$ = new BehaviorSubject<Question[]>([]);
  questionsChanged$ = this.questionsSubject$.asObservable();

  private questionSubject$ = new BehaviorSubject<Question>(null);
  questionChanged$ = this.questionSubject$.asObservable();

  private isProgressSubject$ = new BehaviorSubject<boolean>(false);
  iisProgressChanged$ = this.isProgressSubject$.asObservable();

  private clickSubject: Subject<any> = new Subject<any>();
  clickSubject$ = this.clickSubject.asObservable();

  private userSubject$ = new BehaviorSubject<UserAuthentified>(null);
  quserChanged$ = this.userSubject$.asObservable();

  protected httpRequestState$ = new BehaviorSubject<HttpRequestState>('EMPTY');
  protected message$ = new BehaviorSubject<string | null>(null);

  private pledgeOptionsSubject$ = new BehaviorSubject<PledgeOptions>(null);
  pledgeOptionsChanged$ = this.pledgeOptionsSubject$.asObservable();

  items$ = this.loadingService
    .showLoadingUntilCompleted(this.pledgeDao.getPledges$())
    .pipe(take(1));
  crudService = new CrudService<PledgeRead>(this.items$);
  constructor(
    private pledgeDao: PledgeDao,
    private loadingService: LoadingService,
    private eventBus: EventBusService,
    private documentDao: DocumentDao,
  ) {}

  setOwner(owners: Partial<Owner[]>) {
    const updatedOwners = owners?.map((owner: Owner) => {
      const existingOwner = this.getEntity().find(
        (ownerEntity: Owner) => ownerEntity?.id === owner?.id,
      );
      if (existingOwner) {
        return { ...existingOwner, ...owner };
      }
      return owner;
    });
    this.ownerSubject$.next(updatedOwners);

    this.crudService.setCurrentEntity({
      ...this.getPledge(),
      owners: updatedOwners,
    });
  }
  getIsProgress() {
    return this.isProgressSubject$.getValue();
  }
  setIsProgress(value: boolean) {
    return this.isProgressSubject$.next(value);
  }
  getPledge(): PledgeRead {
    return this.crudService.getCurrentEntitySubject().getValue();
  }
  getPladge$(): BehaviorSubject<PledgeRead> {
    return this.crudService.getCurrentEntitySubject();
  }
  setPledge(pledge: PledgeRead): void {
    this.crudService.setCurrentEntity(_.omit(pledge, 'commands'));
  }
  setQuestions(questions: Question[]) {
    this.questionsSubject$.next(questions);
  }
  getQuestions() {
    return this.questionsSubject$.getValue();
  }

  setUser(user: UserAuthentified) {
    this.userSubject$.next(user);
  }
  getUser() {
    return this.userSubject$.getValue();
  }
  setQuestion(question: Question) {
    this.questionSubject$.next(question);
  }
  getQuestion() {
    return this.questionSubject$.getValue();
  }
  setPledgeOptions(pledgeOptions: PledgeOptions) {
    this.pledgeOptionsSubject$.next(pledgeOptions);
  }
  getPledgeOptions(): PledgeOptions {
    return this.pledgeOptionsSubject$.getValue();
  }
  getPledgeOptions$(): Observable<PledgeOptions> {
    return this.pledgeDao.getPledgeOptions$();
  }
  notifyClick() {
    this.clickSubject.next(1);
  }
  isOwners(owners: DataType): owners is Owner[] {
    return (owners as Owner[])?.length > 0;
  }
  isProducts(products: DataType): products is Product[] {
    return (products as Product[])?.length > 0;
  }
  isParameters(parameters: DataType): parameters is Parameters {
    return parameters && Object.keys(parameters as Parameters)?.length > 0;
  }
  savePledge(data: DataType): Observable<PledgeRead> {
    const pledge = this.getPledge();
    const { owners, products, parameters } = pledge ?? {};
    const updatedOwners = this.getNewOwner(data, owners, pledge);
    const updatedProducts = this.getNewProduct(data, products, pledge);
    const updatedParameters = this.getNewParameters(data, parameters, pledge);
    const newPledge = this.getNewPledge(
      pledge,
      updatedOwners,
      updatedProducts,
      updatedParameters,
    );

    return this.pledgeDao.savePledge({ ...newPledge }).pipe(
      tap((pledge: PledgeRead) => {
        this.afterSaved(pledge);
      }),
    );
  }
  private afterSaved(pledge: PledgeRead) {
    this.setPledge(pledge);
    this.commandsSubject$.next(null);
    this.stepNameSubject$.next(null);
    this.crudService.updateItem(pledge);
  }

  putPledge(pledge: PledgeRead): Observable<PledgeRead> {
    return this.pledgeDao.savePledge({ ...pledge }).pipe(
      tap((pledge: PledgeRead) => {
        this.afterSaved(pledge);
      }),
    );
  }

  scrollToTop() {
    const element = document.querySelector('.subscription');
    element.scrollIntoView();
  }
  savePledgeAndResetProduct(obj: DataType): Observable<PledgeRead> {
    const pledge = this.getPledge();
    const { owners, parameters } = pledge ?? {};
    let newOwners = this.getNewOwner(obj, owners, pledge);
    let newParameters = this.getNewParameters(obj, parameters, pledge);
    const newProducts: Product[] = [];

    const newPledge = this.getNewPledge(
      pledge,
      newOwners,
      newProducts,
      newParameters,
    );

    return this.pledgeDao.savePledge({ ...newPledge }).pipe(
      tap((savedPledge: PledgeRead) => {
        this.afterSaved(savedPledge);
      }),
    );
  }

  saveOwners(pledgeRead: PledgeRead) {
    const pledge = this.getPledge();
    const { products, parameters } = pledge ?? {};
    const owners = pledgeRead.owners.map((owner: Owner) => {
      const entity = pledge?.owners?.find(
        (pledgeOwner: Owner) => pledgeOwner.ownerId === owner.ownerId,
      );

      if (entity) {
        return {
          ...entity,
          ...owner,
          antiMoneyLaundering: {
            ...entity?.antiMoneyLaundering,
            ...owner?.antiMoneyLaundering,
          },
          company:
            parameters.subscriberType === SubscriberTypeEnum.COMPANY
              ? {
                  ...entity?.company,
                  ...owner?.company,
                }
              : undefined,
        };
      }

      return {
        ...owner,
        antiMoneyLaundering: {
          ...owner?.antiMoneyLaundering,
        },
        company:
          parameters.subscriberType === SubscriberTypeEnum.COMPANY
            ? { ...owner?.company }
            : undefined,
      };
    });

    const newPledge: PledgeRead = {
      ...pledge,
      owners,
      products,
      parameters,
      step: String(this.eventBus.getStep()),

      commands: this.commandsSubject$.getValue(),
      stepName: this.stepNameSubject$.getValue(),
    };

    return this.pledgeDao.savePledge({ ...newPledge }).pipe(
      tap((pledge: PledgeRead) => {
        this.afterSaved(pledge);
      }),
    );
  }

  saveProducts(pledgeRead: PledgeRead) {
    const pledge = this.getPledge();
    const { owners, parameters } = pledge ?? {};
    const products = pledgeRead.products.map((product: Product) => {
      const entity = pledge?.products?.find(
        (item: Product) => item.productId === product.productId,
      );

      if (entity) {
        return {
          ...entity,
          ...product,
          type: pledge.productType,
          operation: pledge.operation,
          parameters: {
            ...entity?.parameters,
            ...product?.parameters,
            type: pledge.productType,
            operation: pledge.operation,
            loan:
              pledge.productType === 'BORROWER_INSURANCE'
                ? {
                    ...entity?.parameters?.loan,
                    ...product?.parameters?.loan,
                  }
                : undefined,
          },
        };
      }

      return {
        ...product,
        type: pledge.productType,
        operation: pledge.operation,
        parameters: {
          ...product?.parameters,
          type: pledge.productType,
          operation: pledge.operation,
        },
      };
    });

    const newPledge: PledgeRead = {
      ...pledge,
      owners,
      products,
      // Une slide produit peut aussi porter un paramètre de souscription
      // (viager, …) ; les extractions qui n'en renvoient pas laissent
      // `parameters` inchangé.
      parameters: { ...parameters, ...pledgeRead?.parameters },
      step: String(this.eventBus.getStep()),

      commands: this.commandsSubject$.getValue(),
      stepName: this.stepNameSubject$.getValue(),
    };

    return this.pledgeDao.savePledge({ ...newPledge }).pipe(
      tap((pledge: PledgeRead) => {
        this.afterSaved(pledge);
      }),
    );
  }
  private getNewParameters(
    obj: DataType,
    newParameters: Parameters,
    pledge: PledgeRead,
  ) {
    if (this.isParameters(obj)) {
      const parameters = obj as Parameters;
      newParameters = { ...pledge.parameters, ...parameters };
    }
    const omittedObj = _.omit(newParameters, ['owners', 'products', '0']);
    return omittedObj as Parameters;
  }

  private getNewProduct(
    data: DataType,
    newProducts: Product[],
    pledge: PledgeRead,
  ) {
    if (this.isProducts(data)) {
      newProducts = pledge?.products?.map((product: Product) => {
        const entity = data?.find(
          (d: Product) => d.productId === product.productId,
        );

        if (entity) {
          return {
            ...product,
            ...entity,
            type: pledge.productType,
            operation: pledge.operation,
            parameters: {
              ...product?.parameters,
              ...entity?.parameters,
              type: pledge.productType,
              operation: pledge.operation,
              loan:
                pledge.productType === 'BORROWER_INSURANCE'
                  ? {
                      ...product?.parameters?.loan,
                      ...entity?.parameters?.loan,
                    }
                  : undefined,
            },
          };
        }

        return {
          ...product,
          type: pledge.productType,
          operation: pledge.operation,
          parameters: {
            ...product?.parameters,
            type: pledge.productType,
            operation: pledge.operation,
          },
        };
      });
    }

    return newProducts;
  }

  private getNewOwner(data: DataType, newOwners: Owner[], pledge: PledgeRead) {
    if (this.isOwners(data)) {
      newOwners = pledge?.owners?.map((pledgeOwner: Owner) => {
        const item = data?.find(
          (item: Owner) => item?.ownerId === pledgeOwner?.ownerId,
        );

        const mergerdItem = {
          ...pledgeOwner,
          ...item,
          riskProfil: {
            ...pledgeOwner?.riskProfil,
            ...item?.riskProfil,
          },
          profilEmprunteur: {
            ...pledgeOwner?.profilEmprunteur,
            ...item?.profilEmprunteur,
          },
          antiMoneyLaundering: {
            ...pledgeOwner?.antiMoneyLaundering,
            ...item?.antiMoneyLaundering,
          },
        };

        if (item) {
          return mergerdItem;
        }
        return pledgeOwner;
      });
    }
    return newOwners;
  }

  private getNewPledge(
    pledge: PledgeRead,
    newOwners: Owner[],
    newProducts: Product[],
    newParameters: Parameters,
  ) {
    return {
      ...pledge,
      owners: newOwners,
      products: newProducts,
      parameters: newParameters,
      step: '' + this.eventBus.getStep(),

      commands: this.commandsSubject$.getValue(),
      stepName: this.stepNameSubject$.getValue(),
    };
  }

  getEntity(): Owner[] {
    return this.ownerSubject$.value || [];
  }

  clearEntity(): void {
    this.ownerSubject$.next(null);
  }
  clearPledge(): void {
    this.crudService.setCurrentEntity(null);
  }

  getPledges$: Observable<PledgeRead[]> = this.loadingService
    .showLoadingUntilCompleted(this.pledgeDao.getPledges$())
    .pipe(take(1));

  pledgesWithCRUD$: Observable<PledgeRead[]> = this.crudService.itemsWithCRUD$;

  emitPledge(pledge: PledgeRead): void {
    this.setPledge(pledge);

    this.emitStep(pledge);
  }
  emitJustPledge(pledge: PledgeRead): void {
    this.setPledge(pledge);
  }
  emitStep(pledge: PledgeRead): void {
    pledge?.step
      ? this.eventBus.setStep(~~pledge.step)
      : this.eventBus.clearStep();
  }
  emitPledgeAndEntity(pledge: PledgeRead): void {
    this.emitPledge(pledge);
  }
  addPledge(pledge: PledgeRead): void {
    this.emitPledge(pledge);
    this.crudService.addItem(pledge);
  }

  updatePledge(pledge: PledgeRead): void {
    this.emitPledge(pledge);
    this.crudService.updateItem(pledge);
  }
  setCommand(commands: CommandEnum[]): void {
    this.commandsSubject$.next(commands);
  }
  setStepName(stepName: string): void {
    this.stepNameSubject$.next(stepName);
  }
  clearStepNameAndCommands() {
    //  this.stepNameSubject$.next(null);
    this.commandsSubject$.next(null);
  }
  getStepName() {
    return this.stepNameSubject$.getValue();
  }

  initPledge(params: PledgeInitParams): Observable<PledgeRead> {
    // clean old pledge with commands in rxjs memory
    this.clearStepNameAndCommands();
    const {
      productType,
      productName,
      ownerIds,
      productIds,
      sellerIds,
      physicalUser = true,
      gridkyPropertyId = undefined,
      loanId = undefined,
      borrowerInsuranceContext = undefined,
      borrowerInsuranceMode = undefined,
      portfolioSimulationParameters = undefined,
      operationAssetContractId = undefined,
      operation = 'SUBSCRIPTION',
      scope = null,
      sellerLegalEntities = undefined,
      attachedLegalEntityId = undefined,
    } = params || {};
    const observable$ = this.pledgeDao
      .initPledge(
        productType,
        productName,
        ownerIds,
        productIds,
        sellerIds,
        physicalUser,
        gridkyPropertyId,
        loanId,
        borrowerInsuranceContext,
        borrowerInsuranceMode,
        portfolioSimulationParameters,
        operationAssetContractId,
        operation,
        scope,
        undefined,
        sellerLegalEntities,
        attachedLegalEntityId,
      )
      .pipe(
        take(1),

        tap((pledge: PledgeRead) => {
          if (pledge) {
            this.addPledge(pledge);
          }
        }),
      );
    const loadData$ =
      this.loadingService.showLoadingUntilCompleted(observable$);
    return loadData$;
  }
  deletePledge(pledge: PledgeRead): Observable<PledgeRead> {
    const observable$ = this.pledgeDao.deletePledge(pledge).pipe(
      tap(pledge => {
        this.crudService.deleteItem(pledge);
      }),
    );
    const loadData$ =
      this.loadingService.showLoadingUntilCompleted(observable$);
    return loadData$;
  }
  setAccountInformations(
    additionalQuestionsObject: AdditionalQuestionsObject,
  ): Observable<PledgeRead> {
    const pledge = this.getPledge();

    pledge.accountInformations = {
      ...pledge.accountInformations,
      ...{ ...additionalQuestionsObject.accountInformations },
    };

    return this.putPledge({ ...pledge });
  }
  getSaveMethod() {
    return {
      products: this.saveProducts.bind(this),
      owners: this.saveOwners.bind(this),
      pledge: this.savePledge.bind(this),
      pledgeAndResetProduct: this.savePledgeAndResetProduct.bind(this),
      accountInformations: this.setAccountInformations?.bind(this),
    };
  }

  submitPledge(
    prForm: PrFormAbstract,
    type: SubmitionType = 'pledge',
    withNextStep = true,
    callback: () => any = undefined,
    formValid: boolean = false,
  ): void {
    prForm.displayErrors();
    const valid = !formValid ? prForm.form.valid : formValid;
    const dirty = !formValid ? prForm.form.dirty : formValid;

    if (valid) {
      if (dirty) {
        const observable$ = this.getSaveMethod()[type](prForm.extract());

        const loadData$ =
          this.loadingService.showLoadingUntilCompleted(observable$);

        loadData$.subscribe(() => {
          withNextStep && this.eventBus.nextStep();
          if (callback) callback();
        });
      } else {
        this.eventBus.nextStep();
      }
      this.scrollToTop();
    }
  }
  submitPledgeWithoutForm(
    pledge: PledgeRead & { extract?: () => any },
    type: SubmitionType = 'pledge',
  ): void {
    const observable$ = this.getSaveMethod()[type](pledge?.extract?.());

    const loadData$ =
      this.loadingService.showLoadingUntilCompleted(observable$);
    loadData$.subscribe(() => {
      this.eventBus.nextStep();
      this.scrollToTop();
    });
  }
  get pledgeChanged$() {
    return this.crudService.getCurrentEntity();
  }
  private initFormSub?: Subscription;

  initForm(prForm: PrFormAbstract, option = {}): void {
    this.loadingService.loading();
    this.initFormSub?.unsubscribe();
    this.initFormSub = this.crudService.getCurrentEntity().subscribe(pledge => {
      this.loadingService.loaded();
      prForm.entity = pledge || null;
      prForm.initForm(pledge, option);
    });
  }

  initFormWithDocuments(
    prForm: PrFormAbstract,
    type = [
      'CNI',
      'PASSEPORT',
      //  "PERMIS_DE_CONDUIRE", "TITRE_DE_SEJOUR"
    ],
  ) {
    this.loadingService.loading();
    return this.crudService.getCurrentEntity().pipe(
      mergeMap(pledge => {
        return this.documentDao
          .searchDocument(pledge.accountId, {
            identityDocuments: true,
          })
          .pipe(
            take(1),
            map((documents: any) => {
              const sortedDocuments = this.sortByDateDescending(documents);
              const ownerMain = this.getOwner(pledge, true);
              const ownerCoMain = this.getOwner(pledge, false);

              const documentSubscriber = this.getDocumentBySubscriber(
                sortedDocuments,
                ownerMain?.ownerId,
                this.getOwnerIdDocumentId(ownerMain),
              );

              const documentCoSubscriber = this.getDocumentBySubscriber(
                sortedDocuments,
                ownerCoMain?.ownerId,
                this.getOwnerIdDocumentId(ownerCoMain),
              );

              this.updateOwnerDocuments(
                pledge,
                documentSubscriber,
                documentCoSubscriber,
                ownerMain,
                ownerCoMain,
              );

              return pledge;
            }),
            finalize(() => {
              this.loadingService.loaded();
            }),
          );
      }),
    );
  }
  private getOwnerIdDocumentId(owner: Owner | undefined): undefined | number {
    return (
      owner?.gedCniId ||
      owner?.gedPassportId ||
      owner?.gedPermisDeConduireId ||
      owner?.gedTitreDeSejourId
    );
  }
  private sortByDateDescending(documents: any) {
    return documents.sort((a, b) => {
      const dateA = new Date(a.dateCreated) as any;
      const dateB = new Date(b.dateCreated) as any;

      return dateB - dateA;
    });
  }

  private updateOwnerDocuments(
    pledge: PledgeRead,
    documentSubscriber: any,
    documentCoSubscriber: any,
    ownerMain?: Owner,
    ownerCoMain?: Owner,
  ) {
    if (documentCoSubscriber && ownerCoMain) {
      ownerCoMain.documents = documentCoSubscriber;
    }
    if (documentSubscriber && ownerMain) {
      ownerMain.documents = documentSubscriber;
    }
  }
  private getOwner(pledge: PledgeRead, isMain = true): Owner {
    return (
      pledge?.owners?.find((owner: Owner) => owner.isMain === isMain) ||
      pledge?.owners?.[0]
    );
  }

  private getDocumentBySubscriber(
    documents: any[],
    ownerId?: number,
    documentId?: number,
  ) {
    if (ownerId && documentId) {
      return documents
        .filter((doc: any) => doc.ownerIds?.length)
        .find(
          (doc: any) => doc.ownerIds.includes(ownerId) && doc.id === documentId,
        );
    }

    return ownerId && documents?.length
      ? documents
          .filter((doc: any) => doc.ownerIds?.length)
          .find((doc: any) => doc.ownerIds.includes(ownerId))
      : null;
  }
  setupFormValueAmountChanges(
    form: FormGroup,
    fields: { investmentAmount: string; taxReductionAmount: string },
    sessionConsultant: boolean,
  ) {
    const errorSubject = new BehaviorSubject<ValidationErrors>(null);

    form.valueChanges.subscribe(data => {
      if (sessionConsultant) {
        const investmentAmount = form.get(fields.investmentAmount)?.value || 0;
        const taxReductionAmount =
          form.get(fields.taxReductionAmount)?.value || 0;

        const investmentAmountCond = investmentAmount;
        if (!investmentAmountCond) {
          errorSubject.next(null);
          return;
        }
        if (taxReductionAmount && taxReductionAmount < investmentAmountCond) {
          form.get(fields.investmentAmount).setErrors({
            incorrect: true,
            message:
              "Le montant de la réduction d'impôts doit être supérieur au montant de l'investissement",
          });
        } else {
          form.get(fields.investmentAmount).setErrors(null);
        }
        errorSubject.next(form.get(fields.investmentAmount).errors);
      }
    });

    return errorSubject.asObservable();
  }
}
